import { connect } from 'react-redux';
import { goBack, push } from 'connected-react-router';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import RentAmountPage from './component';
import { DEFAULT_ERROR, PAYMENT_SETTINGS_UPDATED } from '@rental-manager/constants/NotificationConstants';
import { getFeaturePermissions, getLandlordStatus, requestUpdateRentTerms } from '@rental-manager/actions/requests';
import { acceptTerms, savePaymentSettings } from '@rental-manager/actions/OnboardingActions';
import paths from '@rental-manager/pathsManifest';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, { propertyId, fromDashboard, listing }) => {
  const { paymentSettings, leasesForProperty, leaseId } = state.payments.landlord.onboarding[propertyId];
  const { currentTerm = {}, selectedLease } = state.payments.landlord.property;
  const { paymentsEncodedLeaseId } = selectedLease || {};
  const { monthlyRent, amount: amountFromPaymentSettings, editMode, requestStatus } = paymentSettings;
  const { baseRent: baseRentFromLease } = leasesForProperty[leaseId] || {};

  const amountFromListingDetails = listing?.floorplans?.[0]?.price;
  const amountFromListingInCents = amountFromListingDetails ? amountFromListingDetails * 100 : 0;
  const finalAmount = amountFromPaymentSettings || amountFromListingInCents;

  return {
    monthlyRent: (fromDashboard ? currentTerm.amount : baseRentFromLease) || monthlyRent || finalAmount,
    requestStatus,
    editMode,
    rentTermsKey: editMode ? currentTerm.rentTermsKey : '',
    config: state.payments.landlord.config,
    paymentsEncodedLeaseId,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
    propertyId,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId, fromDashboard, listing }) => ({
  onSubmit: (paymentSettings, editMode, rentTermsKey, paymentsEncodedLeaseId) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(acceptTerms({ propertyId })))
      .then(() => dispatch(savePaymentSettings({ propertyId, paymentSettings })))
      .then(() => {
        if (editMode) {
          return dispatch(
            requestUpdateRentTerms({
              propertyId,
              rentTermsKey,
              amount: paymentSettings.monthlyRent,
              paymentsEncodedLeaseId,
            }),
          );
        }
      })
      .then(editMode && checkRequestActionSuccess)
      .then(() => editMode && dispatch(getFeaturePermissions({ propertyId })))
      .then(() => {
        if (editMode) {
          return dispatch(push(baseUrl + paths.onboarding.confirmationPage));
        } else {
          return dispatch(push(baseUrl + paths.onboarding.dueDatePage));
        }
      })
      .then(() => editMode && dispatch(showModalNotification(PAYMENT_SETTINGS_UPDATED)))
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      }),
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(goBack())),
  onSubmitFromDashboard: (paymentSettings) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(savePaymentSettings({ propertyId, paymentSettings })))
      .then(() => {
        const paymentLength = paymentSettings.monthToMonth
          ? { monthToMonth: true }
          : { numberOfPayments: paymentSettings.numberOfPayments };

        const rentTerms = {
          propertyId,
          amount: paymentSettings.monthlyRent,
          firstDueDate: formatDate(paymentSettings.firstDueDate, 'yyyy-MM-dd 00:00'),
          ...paymentLength,
        };

        return dispatch(
          requestUpdateRentTerms({
            propertyId,
            rentTerms,
          }),
        );
      })
      .then(checkRequestActionSuccess)
      .then(() => dispatch(push(baseUrl)))
      .then(() => dispatch(getLandlordStatus({ propertyId })))
      .then(() => dispatch(showPageNotification(PAYMENT_SETTINGS_UPDATED)))
      .catch((err) => {
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      }),
  onCancel: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const newUrl = fromDashboard ? baseUrl : baseUrl + paths.onboarding.confirmationPage;
        return dispatch(push(newUrl));
      }),
  onClose: (paymentSettings) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(savePaymentSettings({ propertyId, paymentSettings })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup))),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps, { fromDashboard }) => ({
  ...stateProps,
  ...dispatchProps,
  onSubmit: (paymentSettings) =>
    fromDashboard
      ? dispatchProps.onSubmitFromDashboard(paymentSettings)
      : dispatchProps.onSubmit(
          paymentSettings,
          stateProps.editMode,
          stateProps.rentTermsKey,
          stateProps.paymentsEncodedLeaseId,
        ),
  onClose: () => dispatchProps.onClose(stateProps.editMode),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(RentAmountPage);
